import { TemplateType } from "@redotech/redo-marketing-templates-common/email-builder";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { sendEmailFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isSendEmailStepValid,
  SendEmailState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import EmailIcon from "@redotech/redo-web/icon-old/send-email.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";
import { getTemplateTypeLabel } from "./template-type-label";

export const SEND_EMAIL = (
  Details: React.ComponentType<StepTypeDetailsProps<SendEmailState>>,
): StepType<SendEmailState, ModelStep.SendEmail> => ({
  Details: (props: StepTypeDetailsProps<SendEmailState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.SEND_EMAIL],
  valid(state) {
    return isSendEmailStepValid(state).valid;
  },
  Icon: EmailIcon,
  description(state) {
    return state?.emailName && state?.emailType
      ? `${state.emailName} - ${getTemplateTypeLabel(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          state.emailType as TemplateType,
        )}`
      : "";
  },
  empty: { next: undefined, emailId: "" },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Send Email step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      emailId: state.emailId,
      emailName: state.emailName,
      emailType: state.emailType,
      type: ModelStep.SEND_EMAIL,
      next: state.next ? id(state.next) : undefined,
    };
  },
  fromModel(model, id) {
    return sendEmailFromModel(model, id);
  },
});
